import { useState, useEffect } from 'react';
import { adminUserAPI } from '../lib/admin-api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, User, Mail, Shield, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export function AgentLister() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string[]>([]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const allUsers = await adminUserAPI.getUsers();
      setUsers(allUsers);
      console.log('📋 All users in system:', allUsers);
    } catch (error) {
      logger.error('Error loading users', error as Error, undefined, 'AgentLister');
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId: string, userName: string) => {
    if (!confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(prev => [...prev, userId]);
    try {
      await adminUserAPI.deleteUser(userId);
      toast.success(`Deleted ${userName}`);
      await loadUsers(); // Reload the list
    } catch (error: any) {
      logger.error('Error deleting user', error as Error, undefined, 'AgentLister');
      toast.error(`Failed to delete ${userName}: ${error.message}`);
    } finally {
      setDeleting(prev => prev.filter(id => id !== userId));
    }
  };

  // Separate users by role
  const agents = users.filter(u => 
    u.role === 'Agent' || u.role === 'agent' || 
    u.groups?.includes('agent') ||
    u.cognitoGroups?.includes('agent')
  );
  
  const admins = users.filter(u => 
    u.role === 'Admin' || u.role === 'admin' || 
    u.groups?.includes('admin') || 
    u.cognitoGroups?.includes('admin')
  );
  
  const others = users.filter(u => 
    !agents.includes(u) && !admins.includes(u)
  );

  // Identify potential placeholder/test users
  const isPlaceholder = (user: any) => {
    const email = user.email?.toLowerCase() || '';
    const name = user.name?.toLowerCase() || '';
    const id = user.id?.toLowerCase() || '';
    
    return (
      email.includes('test') ||
      email.includes('demo') ||
      email.includes('sample') ||
      email.includes('placeholder') ||
      name.includes('test') ||
      name.includes('demo') ||
      name.includes('sample') ||
      id.includes('test') ||
      id.includes('demo') ||
      id.includes('sample') ||
      // Common test accounts
      email.includes('sarah.jones') ||
      email.includes('mike.wilson') ||
      email.includes('john.doe')
    );
  };

  const UserCard = ({ user, showDelete = true }: { user: any; showDelete?: boolean }) => {
    const isPlaceholderUser = isPlaceholder(user);
    const isDeleting = deleting.includes(user.id);

    return (
      <Card className={`p-4 ${isPlaceholderUser ? 'border-orange-500/50 bg-orange-500/5' : ''}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-[#FDB022]/20 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-[#FDB022]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-medium truncate">{user.name}</h4>
                {isPlaceholderUser && (
                  <Badge variant="outline" className="text-xs border-orange-500 text-orange-500">
                    Placeholder
                  </Badge>
                )}
                {user.status === 'Inactive' && (
                  <Badge variant="destructive" className="text-xs">
                    Inactive
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
                <Mail className="w-3.5 h-3.5" />
                <span className="truncate">{user.email}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="text-xs">
                  <Shield className="w-3 h-3 mr-1" />
                  {user.role || 'No Role'}
                </Badge>
                {user.groups && user.groups.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Groups: {user.groups.join(', ')}
                  </Badge>
                )}
                {user.cognitoGroups && user.cognitoGroups.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Cognito: {user.cognitoGroups.join(', ')}
                  </Badge>
                )}
              </div>
              <div className="mt-2 text-xs text-muted-foreground font-mono">
                ID: {user.id}
              </div>
            </div>
          </div>
          {showDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deleteUser(user.id, user.name)}
              disabled={isDeleting}
              className="flex-shrink-0 text-destructive hover:text-destructive"
              data-testid="agent-lister-delete-user-button"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </Button>
          )}
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FDB022]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">System Users Report</h1>
        <p className="text-muted-foreground">
          Total users in system: <strong>{users.length}</strong>
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-2xl font-bold text-[#FDB022]">{admins.length}</div>
          <div className="text-sm text-muted-foreground">Admins</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-[#FDB022]">{agents.length}</div>
          <div className="text-sm text-muted-foreground">Agents</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-[#FDB022]">{others.length}</div>
          <div className="text-sm text-muted-foreground">Other Roles</div>
        </Card>
      </div>

      {/* Admins Section */}
      {admins.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#FDB022]" />
            Admins ({admins.length})
          </h2>
          <div className="space-y-3">
            {admins.map(user => (
              <UserCard key={user.id} user={user} />
            ))}
          </div>
        </div>
      )}

      {/* Agents Section */}
      {agents.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-[#FDB022]" />
            Agents ({agents.length})
          </h2>
          <div className="space-y-3">
            {agents.map(user => (
              <UserCard key={user.id} user={user} />
            ))}
          </div>
        </div>
      )}

      {/* Other Users Section */}
      {others.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">
            Other Users ({others.length})
          </h2>
          <div className="space-y-3">
            {others.map(user => (
              <UserCard key={user.id} user={user} />
            ))}
          </div>
        </div>
      )}

      {/* Placeholder Detection Summary */}
      {users.filter(isPlaceholder).length > 0 && (
        <Card className="p-6 border-orange-500/50 bg-orange-500/5">
          <h3 className="text-lg font-semibold mb-2 text-orange-600">
            ⚠️ Placeholder/Test Users Detected
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Found {users.filter(isPlaceholder).length} users that appear to be test/placeholder accounts.
            These are highlighted in orange above. Review and delete them if they're not needed.
          </p>
          <div className="text-xs text-muted-foreground">
            Detection criteria: email/name contains "test", "demo", "sample", "placeholder", or common test names like "sarah.jones", "mike.wilson", "john.doe"
          </div>
        </Card>
      )}

      {users.length === 0 && (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">No users found in the system.</p>
        </Card>
      )}
    </div>
  );
}
